import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPieChart, Pie, Cell,
} from 'recharts';
import {
  ArrowLeft, Download, Users, ClipboardList, Gauge, Clock, CheckCircle2,
  ChevronDown, ChevronRight, Star,
} from 'lucide-react';
import surveyApi from '../surveyApi';
import { ratingPoints, typeMeta } from '../questionTypes';
import { labelFor } from '../../i18n/strings';
import SurveyTrends from './SurveyTrends';
import { roundName } from './SurveyRoundsPanel';

const COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#ec4899', '#14b8a6', '#eab308', '#6366f1', '#f97316'];

const csvEscape = (value) => {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (s.includes(',') || s.includes('\n') || s.includes('"')) return `"${s.replace(/"/g, '""')}"`;
  return s;
};
const downloadCsv = (filename, csvContent) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
const fmtDuration = (sec) => {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60); const s = sec % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
};
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');
const LANG_PILL = 'px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-700 text-slate-300 uppercase';

export default function SurveyResults() {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [tab, setTab] = useState('results'); // results | trends
  // round_id '' = current round (server default), 'all' = every round.
  const [scope, setScope] = useState({ round_id: '', language: '' });
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setRefreshing(true);
        const d = await surveyApi.adminResults(surveyId, scope);
        if (active) { setData(d); setError(''); }
      } catch (e) {
        if (active) setError(e.message || 'Failed to load results');
      } finally {
        if (active) { setLoading(false); setRefreshing(false); }
      }
    })();
    return () => { active = false; };
  }, [surveyId, scope]);

  // What the loaded data actually covers (the server resolves '' to the current round).
  const resolvedScope = data ? { round_id: data.scope.round_id, language: data.scope.language || '' } : scope;
  const scopeKey = `${resolvedScope.round_id}|${resolvedScope.language}`;
  const scopeLabel = () => {
    if (!data) return '';
    const r = data.rounds.find((x) => x.id === data.scope.round_id);
    const parts = [r ? roundName(r) : 'All rounds'];
    if (data.scope.language) parts.push(labelFor(data.scope.language));
    return parts.join(' · ');
  };
  const fileTag = () => {
    if (!data) return '';
    const r = data.rounds.find((x) => x.id === data.scope.round_id);
    return `${r ? `_round${r.round_number}` : '_allrounds'}${data.scope.language ? `_${data.scope.language}` : ''}`;
  };

  const exportSummary = () => {
    if (!data) return;
    const rows = [['Question', 'Type', 'Answer / value', 'Count', 'Percent']];
    const total = data.summary.completed || 0;
    data.questions.forEach((q) => {
      if (q.question_type === 'section') return;
      const denom = q.answered_count || 0;
      if (q.question_type === 'ranking' && q.options) {
        q.options.forEach((o) => rows.push([q.question_text, 'ranking', o.option_text,
          o.first_place_count, o.average_rank != null ? `avg rank ${o.average_rank}` : '']));
      } else if (q.options) {
        q.options.forEach((o) => rows.push([q.question_text, q.question_type, o.option_text, o.count,
          denom ? `${Math.round((o.count / denom) * 1000) / 10}%` : '0%']));
      } else if (q.distribution) {
        q.distribution.forEach((d) => rows.push([q.question_text, q.question_type, d.value, d.count,
          denom ? `${Math.round((d.count / denom) * 1000) / 10}%` : '0%']));
        if (q.average != null) rows.push([q.question_text, q.question_type, 'Average', q.average, '']);
        if (q.nps) rows.push([q.question_text, 'nps', 'NPS score', q.nps.score, '']);
      } else if (q.responses) {
        rows.push([q.question_text, q.question_type, `${q.responses.length} text responses`, q.answered_count, '']);
      }
    });
    const csv = [`Survey,${csvEscape(data.survey.title)}`, `Scope,${csvEscape(scopeLabel())}`,
      `Completed responses,${total}`, '',
      ...rows.map((r) => r.map(csvEscape).join(','))].join('\n');
    downloadCsv(`survey_${surveyId}${fileTag()}_summary_${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const exportRaw = async () => {
    try {
      setExporting(true);
      const ex = await surveyApi.adminExport(surveyId, resolvedScope);
      const header = ['Round', 'Language', 'Respondent', 'Submitted', 'Duration (s)', ...ex.questions.map((q) => q.question_text)];
      const lines = [header.map(csvEscape).join(',')];
      ex.rows.forEach((row) => {
        const cells = [row.round_number, row.language, row.respondent, fmtDate(row.submitted_at), row.duration_seconds,
          ...ex.questions.map((q) => row.values[q.id] || '')];
        lines.push(cells.map(csvEscape).join(','));
      });
      downloadCsv(`survey_${surveyId}${fileTag()}_responses_${new Date().toISOString().slice(0, 10)}.csv`, lines.join('\n'));
    } catch (e) {
      setError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-400">Loading…</div>;
  if (error && !data) return <div className="p-8 text-rose-300">{error}</div>;
  if (!data) return null;

  const { survey, summary, timeline, questions, rounds } = data;
  const languages = survey.languages || [];
  const multiLanguage = languages.length > 1 || Object.keys(summary.by_language || {}).length > 1;

  return (
    <div className="p-8">
      <button onClick={() => navigate('/tiles/ksp_training/admin/surveys')}
        className="text-sm text-slate-400 hover:text-slate-100 flex items-center gap-1 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Surveys
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">{survey.title}</h1>
          <div className="text-sm text-slate-400 mt-1 flex items-center gap-3">
            <span className="capitalize">{survey.status}</span>
            {survey.is_anonymous && <span className="text-emerald-300">Anonymous</span>}
            {rounds.length > 1 && <span>{rounds.length} rounds</span>}
          </div>
        </div>
        {tab === 'results' && <div className="flex gap-2">
          <button onClick={exportSummary}
            className="px-3 py-2 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 text-sm flex items-center gap-1">
            <Download className="w-4 h-4" /> Summary CSV
          </button>
          <button onClick={exportRaw} disabled={exporting}
            className="px-3 py-2 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-sm flex items-center gap-1 disabled:opacity-60">
            <Download className="w-4 h-4" /> {exporting ? 'Exporting…' : 'Raw responses CSV'}
          </button>
        </div>}
      </div>

      {/* Tabs + scope (which round, which language version) */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6 border-b border-slate-700 pb-3">
        <div className="flex gap-1">
          {[['results', 'Results'], ['trends', 'Trends across rounds']].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-3 py-1.5 rounded text-sm font-medium ${
                tab === k ? 'bg-amber-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-sm">
          {tab === 'results' && (
            <select value={String(resolvedScope.round_id)}
              onChange={(e) => setScope((sc) => ({ ...sc, round_id: e.target.value }))}
              className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-slate-100">
              {[...rounds].reverse().map((r) => (
                <option key={r.id} value={String(r.id)}>{roundName(r)} ({r.completed})</option>
              ))}
              {rounds.length > 1 && <option value="all">All rounds combined</option>}
            </select>
          )}
          {multiLanguage && (
            <select value={tab === 'results' ? resolvedScope.language : scope.language}
              onChange={(e) => setScope((sc) => ({ ...sc, language: e.target.value }))}
              className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-slate-100">
              <option value="">All languages</option>
              {languages.map((l) => <option key={l} value={l}>{labelFor(l)}</option>)}
            </select>
          )}
          {refreshing && <span className="text-xs text-slate-500">Loading…</span>}
        </div>
      </div>

      {error && <div className="mb-4 p-3 rounded bg-rose-900/40 border border-rose-700 text-rose-200 text-sm">{error}</div>}

      {tab === 'trends' ? (
        <SurveyTrends surveyId={surveyId} language={scope.language} />
      ) : (
      <>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Kpi icon={<ClipboardList className="w-4 h-4" />} tint="text-amber-300" label="Responses" value={summary.completed}
          sub={`${summary.started} started`} />
        <Kpi icon={<Gauge className="w-4 h-4" />} tint="text-sky-300" label="Response rate"
          value={summary.response_rate != null ? `${summary.response_rate}%` : '—'} />
        <Kpi icon={<Users className="w-4 h-4" />} tint="text-violet-300" label="Audience" value={summary.audience_size} />
        <Kpi icon={<Clock className="w-4 h-4" />} tint="text-emerald-300" label="Avg time" value={fmtDuration(summary.avg_duration_seconds)} />
        <Kpi icon={<CheckCircle2 className="w-4 h-4" />} tint="text-slate-300" label="Last response"
          value={summary.last_response_at ? new Date(summary.last_response_at).toLocaleDateString() : '—'} />
      </div>

      {multiLanguage && !resolvedScope.language && summary.completed > 0 && (
        <div className="rounded-lg bg-slate-800 border border-slate-700 p-4 mb-6">
          <h3 className="text-sm uppercase tracking-wide text-slate-400 mb-3">Responses by language</h3>
          <div className="space-y-2">
            {Object.entries(summary.by_language).sort((a, b) => b[1] - a[1]).map(([l, n], i) => (
              <BarRow key={l} label={labelFor(l)} count={n} total={summary.completed} color={COLORS[i % COLORS.length]} />
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      {timeline && timeline.length > 0 && (
        <div className="rounded-lg bg-slate-800 border border-slate-700 p-4 mb-6">
          <h3 className="text-sm uppercase tracking-wide text-slate-400 mb-3">Responses over time</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={timeline} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip content={<DarkTooltip />} />
                <Bar dataKey="count" name="Responses" fill="#f59e0b" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {summary.completed === 0 && (
        <div className="rounded-lg bg-slate-800 border border-slate-700 p-6 text-slate-400 mb-6">
          No responses yet. Results will appear here as employees respond.
        </div>
      )}

      {/* Per-question results */}
      <div className="space-y-4">
        {questions.map((q, i) => (
          <QuestionResult key={q.id} q={q} index={i} showLanguage={multiLanguage && !resolvedScope.language} />
        ))}
      </div>

      {/* Respondents + non-respondents (remounted per scope so they reload) */}
      <ResponsesSection key={`r${scopeKey}`} surveyId={surveyId} scope={resolvedScope} isAnonymous={survey.is_anonymous} />
      <NonRespondentsSection key={`n${scopeKey}`} surveyId={surveyId} scope={resolvedScope} />

      {/* Reviewer-only: real names tied to responses. Rendered only when the server
          identifies the current user as the designated identity reviewer. */}
      {survey.can_reveal_identity && (
        <IdentifiedResponsesSection key={`i${scopeKey}`} surveyId={surveyId} scope={resolvedScope} fileTag={fileTag()} />
      )}
      </>
      )}
    </div>
  );
}

function Kpi({ icon, label, value, sub, tint }) {
  return (
    <div className="rounded-lg bg-slate-800 border border-slate-700 p-4">
      <div className={`flex items-center gap-2 text-xs uppercase tracking-wide ${tint}`}>{icon}<span>{label}</span></div>
      <div className="mt-2 text-2xl font-bold text-slate-100">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

function DarkTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-800 p-2 border border-slate-700 rounded shadow-md text-xs">
        <p className="text-slate-300 font-medium">{label}</p>
        {payload.map((e, i) => <p key={i} style={{ color: e.color }}>{e.name}: {e.value}</p>)}
      </div>
    );
  }
  return null;
}

// horizontal CSS bar row
function BarRow({ label, count, total, color = '#f59e0b' }) {
  const pct = total ? Math.round((count / total) * 1000) / 10 : 0;
  return (
    <div>
      <div className="flex justify-between text-sm text-slate-200 mb-1">
        <span className="truncate pr-2">{label}</span>
        <span className="text-slate-400 shrink-0">{count} · {pct}%</span>
      </div>
      <div className="w-full bg-slate-700 rounded-full h-2.5">
        <div className="h-2.5 rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

function QuestionResult({ q, index, showLanguage }) {
  const meta = typeMeta(q.question_type);
  const Icon = meta.icon;

  if (q.question_type === 'section') {
    return (
      <div className="pt-2">
        <h2 className="text-lg font-semibold text-amber-300">{q.question_text}</h2>
        {q.help_text && <p className="text-sm text-slate-400 mt-1">{q.help_text}</p>}
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-slate-800 border border-slate-700 p-4">
      <div className="flex items-start justify-between gap-2 mb-3">
        <h3 className="text-slate-100 font-medium">
          <span className="text-slate-500 mr-1">{index + 1}.</span>{q.question_text}
        </h3>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-slate-700 text-slate-300 shrink-0">
          <Icon className="w-3.5 h-3.5" /> {meta.short}
        </span>
      </div>
      <div className="text-xs text-slate-500 mb-3">{q.answered_count} response{q.answered_count === 1 ? '' : 's'}</div>
      <ResultBody q={q} showLanguage={showLanguage} />
    </div>
  );
}

function ResultBody({ q, showLanguage }) {
  const type = q.question_type;
  const total = q.answered_count || 0;

  // single-select choice -> donut + legend
  if (['single_choice', 'dropdown', 'yes_no'].includes(type)) {
    const pieData = q.options.map((o) => ({ name: o.option_text, value: o.count }));
    const hasData = q.options.some((o) => o.count > 0);
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
        <div className="h-52">
          {hasData ? (
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={80} dataKey="value"
                  stroke="#1e293b" strokeWidth={2}>
                  {pieData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip content={<DarkTooltip />} />
              </RechartsPieChart>
            </ResponsiveContainer>
          ) : <div className="h-full flex items-center justify-center text-slate-500 text-sm">No responses</div>}
        </div>
        <div className="space-y-2">
          {q.options.map((o, i) => <BarRow key={o.id} label={o.option_text} count={o.count} total={total} color={COLORS[i % COLORS.length]} />)}
        </div>
      </div>
    );
  }

  // multi-select -> horizontal bars
  if (type === 'multi_choice') {
    return (
      <div className="space-y-2">
        {q.options.map((o, i) => <BarRow key={o.id} label={o.option_text} count={o.count} total={total} color={COLORS[i % COLORS.length]} />)}
      </div>
    );
  }

  // ranking -> leaderboard by average position (best first), bars = #1-place votes
  if (type === 'ranking') {
    const opts = q.options || [];
    const n = q.option_count || opts.length;
    const anyData = opts.some((o) => o.count > 0);
    if (!anyData) return <div className="text-slate-500 text-sm">No responses</div>;
    const maxFirst = Math.max(1, ...opts.map((o) => o.first_place_count || 0));
    return (
      <div className="space-y-2">
        <div className="text-xs text-slate-400 mb-1">
          Ordered best-first by average position (1 = best of {n}). Bars show #1-place votes.
        </div>
        {opts.map((o, i) => {
          const fp = o.first_place_count || 0;
          const pct = Math.round((fp / maxFirst) * 100);
          return (
            <div key={o.id} className="flex items-center gap-3">
              <span className="w-6 shrink-0 text-right text-sm text-slate-400">{i + 1}.</span>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between text-sm text-slate-200 mb-1">
                  <span className="truncate pr-2">{o.option_text}</span>
                  <span className="shrink-0 text-slate-400">avg {o.average_rank ?? '—'} · {fp} first</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2.5">
                  <div className="h-2.5 rounded-full" style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // rating -> avg + distribution
  if (type === 'rating') {
    const points = ratingPoints(q.config);
    const distMap = {}; (q.distribution || []).forEach((d) => { distMap[d.value] = d.count; });
    return (
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-3xl font-bold text-amber-300">{q.average ?? '—'}</span>
          {q.config?.icon !== 'number' && <Star className="w-6 h-6 fill-amber-400 text-amber-400" />}
          <span className="text-sm text-slate-400">avg of {q.config?.scale_max ?? 5}</span>
        </div>
        <div className="space-y-2">
          {points.map((p) => <BarRow key={p} label={`${p}`} count={distMap[p] || 0} total={total} />)}
        </div>
      </div>
    );
  }

  // likert -> avg + distribution by labelled point
  if (type === 'likert') {
    const pts = Array.isArray(q.config?.points) ? q.config.points : [];
    const distMap = {}; (q.distribution || []).forEach((d) => { distMap[d.value] = d.count; });
    return (
      <div>
        <div className="text-sm text-slate-400 mb-3">Average: <span className="text-amber-300 font-semibold">{q.average ?? '—'}</span></div>
        <div className="space-y-2">
          {pts.map((p) => <BarRow key={p.value} label={p.label} count={distMap[p.value] || 0} total={total} />)}
        </div>
      </div>
    );
  }

  // nps -> score + segmented breakdown
  if (type === 'nps') {
    const nps = q.nps || { promoter: 0, passive: 0, detractor: 0, score: 0 };
    const n = total || 1;
    const seg = [
      { key: 'Detractors', count: nps.detractor, color: '#ef4444' },
      { key: 'Passives', count: nps.passive, color: '#f59e0b' },
      { key: 'Promoters', count: nps.promoter, color: '#10b981' },
    ];
    return (
      <div>
        <div className="flex items-center gap-3 mb-3">
          <span className="text-3xl font-bold text-slate-100">{nps.score}</span>
          <span className="text-sm text-slate-400">NPS (−100 to 100)</span>
        </div>
        <div className="flex h-3 rounded-full overflow-hidden mb-2">
          {seg.map((s) => <div key={s.key} style={{ width: `${(s.count / n) * 100}%`, backgroundColor: s.color }} />)}
        </div>
        <div className="flex gap-4 text-xs text-slate-400">
          {seg.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: s.color }} />
              {s.key}: {s.count}
            </span>
          ))}
        </div>
      </div>
    );
  }

  // text -> list
  if (type === 'short_text' || type === 'long_text') {
    const responses = q.responses || [];
    return <TextResponses responses={responses} showLanguage={showLanguage} />;
  }

  return null;
}

function TextResponses({ responses, showLanguage }) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(
    () => responses.filter((r) => r.text.toLowerCase().includes(query.toLowerCase())),
    [responses, query]
  );
  if (responses.length === 0) return <div className="text-sm text-slate-500">No text responses.</div>;
  return (
    <div>
      <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter responses…"
        className="w-full mb-2 px-3 py-1.5 rounded bg-slate-900 border border-slate-700 text-slate-100 text-sm" />
      <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
        {filtered.map((r, i) => (
          <div key={i} className="p-2 rounded bg-slate-900 border border-slate-700 text-sm text-slate-200 whitespace-pre-wrap">
            {showLanguage && r.language && <span className={`${LANG_PILL} mr-2 align-middle`}>{r.language}</span>}
            {r.text}
          </div>
        ))}
        {filtered.length === 0 && <div className="text-sm text-slate-500">No matches.</div>}
      </div>
    </div>
  );
}

function ResponsesSection({ surveyId, scope, isAnonymous }) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState({}); // response_id -> answers
  const [error, setError] = useState('');

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded) {
      try {
        const res = await surveyApi.adminResponses(surveyId, scope);
        setRows(res.responses || []);
        setLoaded(true);
      } catch (e) { setError(e.message || 'Failed to load responses'); }
    }
  };

  const viewDetail = async (rid) => {
    if (detail[rid]) { setDetail((d) => ({ ...d, [rid]: null })); return; }
    try {
      const res = await surveyApi.adminResponseDetail(surveyId, rid);
      setDetail((d) => ({ ...d, [rid]: res.answers || [] }));
    } catch (e) { setError(e.message || 'Failed to load response'); }
  };

  return (
    <div className="mt-8 rounded-lg bg-slate-800 border border-slate-700">
      <button onClick={toggle} className="w-full flex items-center justify-between px-4 py-3 text-slate-200">
        <span className="font-medium flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          Individual responses {isAnonymous ? '(anonymous)' : ''}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4">
          {error && <div className="mb-2 text-rose-300 text-sm">{error}</div>}
          {rows.length === 0 ? (
            <div className="text-slate-400 text-sm">No completed responses.</div>
          ) : (
            <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.response_id} className="rounded bg-slate-900 border border-slate-700">
                  <button onClick={() => viewDetail(r.response_id)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm text-slate-200">
                    <span>{isAnonymous ? r.label : (r.full_name || r.username || `User ${r.user_id}`)}</span>
                    <span className="text-slate-500 flex items-center gap-2">
                      {r.language && <span className={LANG_PILL}>{r.language}</span>}
                      {fmtDate(r.submitted_at)}
                    </span>
                  </button>
                  {detail[r.response_id] && (
                    <div className="px-3 pb-3 space-y-2">
                      {detail[r.response_id].map((a, i) => (
                        <div key={i} className="text-sm">
                          <div className="text-slate-400">{a.question_text}</div>
                          <div className="text-slate-100">
                            {a.option_text != null
                              ? (a.numeric_value != null ? `#${a.numeric_value} — ${a.option_text}` : a.option_text)
                              : a.numeric_value != null ? a.numeric_value
                              : (a.text_value || '—')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NonRespondentsSection({ surveyId, scope }) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [rows, setRows] = useState([]);
  const [anonCount, setAnonCount] = useState(null); // set when the survey is anonymous
  const [error, setError] = useState('');

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded) {
      try {
        const res = await surveyApi.adminNonRespondents(surveyId, scope);
        // Anonymous surveys return only a count (names withheld to prevent deducing
        // who responded from who is missing); named surveys return the person list.
        if (res && res.anonymous) { setAnonCount(res.count); setRows([]); }
        else { setRows(Array.isArray(res) ? res : []); setAnonCount(null); }
        setLoaded(true);
      } catch (e) { setError(e.message || 'Failed to load'); }
    }
  };

  const total = anonCount != null ? anonCount : rows.length;

  return (
    <div className="mt-4 rounded-lg bg-slate-800 border border-slate-700">
      <button onClick={toggle} className="w-full flex items-center justify-between px-4 py-3 text-slate-200">
        <span className="font-medium flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          Who hasn't responded {scope.round_id === 'all' ? 'to the current round' : 'to this round'}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4">
          {error && <div className="mb-2 text-rose-300 text-sm">{error}</div>}
          {total === 0 ? (
            <div className="text-slate-400 text-sm">Everyone in the audience has responded. 🎉</div>
          ) : anonCount != null ? (
            <div className="text-slate-300 text-sm">
              {anonCount} {anonCount === 1 ? 'person hasn’t' : 'people haven’t'} responded yet.
              <span className="text-slate-500"> Names are hidden for anonymous surveys.</span>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {rows.map((u) => (
                <span key={u.id} className="px-2 py-1 rounded bg-slate-900 border border-slate-700 text-sm text-slate-200">
                  {u.full_name || u.username}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Reviewer-only panel: real names tied to responses (any survey) + identified CSV export
// with a blank Department column for manual sorting. Only mounted when
// survey.can_reveal_identity is true, and the endpoints 403 for anyone else.
function IdentifiedResponsesSection({ surveyId, scope, fileTag }) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState({}); // response_id -> answers
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded) {
      try {
        const res = await surveyApi.adminIdentifiedResponses(surveyId, scope);
        setRows(res.responses || []);
        setLoaded(true);
      } catch (e) { setError(e.message || 'Failed to load'); }
    }
  };

  const viewDetail = async (rid) => {
    if (detail[rid]) { setDetail((d) => ({ ...d, [rid]: null })); return; }
    try {
      const res = await surveyApi.adminIdentifiedResponseDetail(surveyId, rid);
      setDetail((d) => ({ ...d, [rid]: res.answers || [] }));
    } catch (e) { setError(e.message || 'Failed to load response'); }
  };

  const downloadIdentified = async () => {
    try {
      setDownloading(true);
      const ex = await surveyApi.adminIdentifiedExport(surveyId, scope);
      const header = ['Response #', 'Round', 'Language', 'Name', 'Username', 'Submitted', 'Duration (s)', 'Department',
        ...ex.questions.map((q) => q.question_text)];
      const lines = [header.map(csvEscape).join(',')];
      ex.rows.forEach((row, i) => {
        const cells = [i + 1, row.round_number, row.language, row.full_name || `User ${row.user_id ?? ''}`, row.username,
          fmtDate(row.submitted_at), row.duration_seconds, '', // blank Department to fill in
          ...ex.questions.map((q) => row.values[q.id] || '')];
        lines.push(cells.map(csvEscape).join(','));
      });
      downloadCsv(`survey_${surveyId}${fileTag}_identified_${new Date().toISOString().slice(0, 10)}.csv`, lines.join('\n'));
    } catch (e) {
      setError(e.message || 'Export failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="mt-4 rounded-lg bg-amber-950/30 border border-amber-800/50">
      <div className="w-full flex items-center justify-between px-4 py-3 gap-3">
        <button onClick={toggle} className="flex items-center gap-2 text-amber-200 font-medium">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          Identified responses — reviewer only
        </button>
        <button onClick={downloadIdentified} disabled={downloading}
          className="px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white text-sm flex items-center gap-1 disabled:opacity-60 shrink-0">
          <Download className="w-4 h-4" /> {downloading ? 'Preparing…' : 'Download identified CSV'}
        </button>
      </div>
      {open && (
        <div className="px-4 pb-4">
          <div className="mb-2 text-xs text-amber-300/80">
            Names are visible only to you. The CSV includes a blank Department column to fill in when
            building the by-department view for leadership.
          </div>
          {error && <div className="mb-2 text-rose-300 text-sm">{error}</div>}
          {rows.length === 0 ? (
            <div className="text-slate-400 text-sm">No completed responses.</div>
          ) : (
            <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.response_id} className="rounded bg-slate-900 border border-slate-700">
                  <button onClick={() => viewDetail(r.response_id)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm text-slate-200">
                    <span>{r.full_name || r.username || `User ${r.user_id}`}</span>
                    <span className="text-slate-500">{fmtDate(r.submitted_at)}</span>
                  </button>
                  {detail[r.response_id] && (
                    <div className="px-3 pb-3 space-y-2">
                      {detail[r.response_id].map((a, i) => (
                        <div key={i} className="text-sm">
                          <div className="text-slate-400">{a.question_text}</div>
                          <div className="text-slate-100">
                            {a.option_text != null
                              ? (a.numeric_value != null ? `#${a.numeric_value} — ${a.option_text}` : a.option_text)
                              : a.numeric_value != null ? a.numeric_value
                              : (a.text_value || '—')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
